// src/Contexts/AuthWrapperContext.tsx
/* eslint-disable react-refresh/only-export-components -- file exports context, provider, and enums */
/**
 * React adapter over AuthService — see documentation/auth-architecture.md.
 *
 * AuthService owns authentication state (a tri-state machine derived from
 * token storage). This context subscribes to it via useSyncExternalStore and
 * layers on the form flows (sign-up, verification, password reset) that are
 * inherently UI concerns. No auth state is duplicated here: isAuthenticated,
 * the user profile, and tokens all come from the service on demand.
 */
import { clearAuthData } from 'Actions/auth';
import { clearLessonData } from 'Actions/lesson';
import { clearRepertoireData } from 'Actions/repertoire';
import { authenticatedStartup, clearAppData } from 'Actions/app';
import { PURGE } from 'redux-persist';
import React, { useEffect } from 'react';
import { AuthProvider, useAuth, hasAuthParams } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'Reducers';
import { CognitoUserEtc, MainAppReducer } from 'Types';
import type { UpdateAuthDataValues, ChangePasswordData, ForgotPasswordData } from 'Types';
import { AuthReducer, ConfirmSignUpPayload, SignUpPayload } from 'Types/AuthTypes';
import {
  authService,
  oidcManager,
  AuthProfile,
  isInvalidGrantOrUnauthorized,
} from 'Utils/AuthService';
import * as authActions from 'Actions/auth';
import { SigninRedirectArgs, OidcClient } from 'oidc-client-ts';
import * as amplify from 'Utils/Amplify';
import { AuthError } from 'aws-amplify/auth';
import { handleAuthErrors } from 'Utils/Forms';
import { awsRum } from 'Utils/AwsRum';
import { reportError } from 'Utils/reportError';
import { isEmail, isPhoneNumber } from 'Components/Forms/SignUpForm';
import { isCapacitorNative } from 'Utils/Constants';
import { filterFalsy } from 'Utils';
import { has, pick } from 'lodash';
import { codeFromCountryAcronym } from 'Components/Forms/CountryCodeSelect';
import CapacitorStorage from 'Utils/CapacitorStorage';
import { clearTierMemory } from 'Utils/TierMemory';
import { Browser } from '@capacitor/browser';
import logger from 'Utils/Logger';
import {
  setLastLoginMethod,
  setPendingOauthMethod,
  commitOauthLoginMethod,
} from 'Utils/LastLoginMethod';

// Re-exported for existing importers; the canonical home is AuthService.
export { ScopesEnum, userScopes, oidcCache, oidcManager } from 'Utils/AuthService';

const AUTH_BOOTSTRAP_WATCHDOG_MS = 10000;

// sessionStorage survives the same-tab OAuth redirect roundtrip and dies with
// the tab — exactly the lifetime "an OAuth flow is in flight" should have.
// (Persisting it to disk let crashed flows poison later sessions.)
const OAUTH_PENDING_STORAGE_KEY = 'museflow:oauth-pending';

function readOauthPendingFlag(): boolean {
  try {
    return sessionStorage.getItem(OAUTH_PENDING_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeOauthPendingFlag(pending: boolean): void {
  try {
    if (pending) {
      sessionStorage.setItem(OAUTH_PENDING_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(OAUTH_PENDING_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

function profileToLegacyUser(
  profile: AuthProfile | undefined,
): Partial<CognitoUserEtc> | undefined {
  if (!profile) return undefined;
  // Intentionally no signInUserSession: tokens are never exposed through
  // state. Call currentAuthToken() (or rely on the axios interceptor).
  return {
    username: profile.email || '',
    attributes: {
      email: profile.email,
      sub: profile.sub,
      phone_number: profile.phone_number,
      name: profile.name,
    },
  };
}

// SetErrorsPayload contains all types of form fields for any auth related form
export interface SetErrorsPayload {
  code: string;
  emailOrPhone: string;
  name: string;
  password: string;
  repeatPassword: string;
  unknown: string;
  currentPassword: string;
  newPassword: string;
  repeatNewPassword: string;
}

const defaultErrors: SetErrorsPayload = {
  code: '',
  emailOrPhone: '',
  name: '',
  password: '',
  repeatPassword: '',
  unknown: '',
  currentPassword: '',
  newPassword: '',
  repeatNewPassword: '',
};

export enum SIGN_IN_COMPONENT {
  SIGNIN = 'SIGNIN',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD',
  VERIFICATION_CODE = 'VERIFICATION_CODE',
}

export interface AuthState {
  user: Partial<CognitoUserEtc> | undefined;
  confirmationCode: string | undefined;
  usernameAuthPending: boolean;
  processPending: boolean;
  errors: SetErrorsPayload;
  resetEmailOrPhone: string | undefined;
  resetCountryAcronym: string | undefined;
  pendingEmailOrPhone: string | undefined;
  pendingCountryAcronym: string | undefined;
  pendingEmailOrPhoneSuccessful: boolean;
  signInComponent: SIGN_IN_COMPONENT;
  successMessage: string | undefined;
  failureMessage: string | undefined;
  isAuthenticated: boolean;
  authInitialized: boolean;
  isLoading?: boolean;
  oauthPending?: boolean;
  tempSignUpPass: string | undefined;
}

type ContextFunctions = {
  currentAuthToken: () => Promise<string | undefined>;
  user?: Partial<CognitoUserEtc>;
  logOut: () => void;
  signInRedirect: (args?: SigninRedirectArgs) => Promise<void>;
  logInUsername: (emailOrPhone: string, password: string, countryAcronym: string) => void;
  refreshUser: () => Promise<void>;
  verifyAttributes: () => Promise<void>;
  verifyAttributesSubmit: (code: string) => Promise<void>;
  updateAuthDataCognito: (updateAuthData: { payload: UpdateAuthDataValues }) => Promise<void>;
  changePassword: (changePassword: { payload: ChangePasswordData }) => Promise<void>;
  forgotPassword: (forgotPassword: { payload: ForgotPasswordData }) => Promise<void>;
  submitNewPassword: (submitNewPassword: { payload: { password: string } }) => Promise<void>;
  verifyPendingEmailOrPhone: (code: string) => Promise<void>;
  resendSignUpUsername: (emailOrPhone: string) => Promise<void>;
  confirmSignUpUsername: (evt: ConfirmSignUpPayload) => Promise<void>;
  signUpUsername: (evt: SignUpPayload) => Promise<void>;
  logInOidc: (provider: 'Google' | 'SignInWithApple') => Promise<void>;
  resetErrors: () => void;
  setConfirmationCode: (code: string | undefined) => void;
  setSignInComponent: (component: SIGN_IN_COMPONENT) => void;
  setPendingEmailOrPhone?: (val: string | undefined) => void;
  isOidcUser?: boolean;
};

type AuthContextState = ContextFunctions & AuthState;

export const AuthWrapperContext = React.createContext<AuthContextState | null>(null);

export const useAuthWrapperContext = () => {
  const context = React.useContext(AuthWrapperContext);
  if (!context) {
    throw new Error('useAuthWrapper must be used within an AuthWrapperProvider');
  }
  return context;
};

const AuthWrapperProvider = ({ children }: { children: React.ReactNode }) => {
  const oidcAuth = useAuth();
  const reduxAuth = useSelector((state: AuthReducer) => state.authReducer);
  // Signed-in marker, not the userData object: userData is always a truthy
  // defaults object (reset by CLEAR_DATA), so testing it never closes the
  // reconciliation guard below. userDataFetched is true only after an
  // authenticated fetch and flips back to false on purge. (MUS-264)
  const hasSignedInData = useSelector(
    (state: MainAppReducer) => state.mainAppReducer.userDataFetched,
  );
  const dispatch = useDispatch<AppDispatch>();

  // The one auth source of truth, subscribed — never copied into other state.
  const snapshot = React.useSyncExternalStore(authService.subscribe, authService.getSnapshot);
  const isAuthenticated = snapshot.status === 'authenticated';
  const authInitialized = snapshot.status !== 'resolving';
  const user = React.useMemo(() => profileToLegacyUser(snapshot.profile), [snapshot.profile]);

  // UI-only form-flow state. Transient by design; none of it is persisted
  // except tempSignUpPass (redux), which must survive the verify-email tab.
  const [confirmationCode, setConfirmationCode] = React.useState<string | undefined>(undefined);
  const [usernameAuthPending, setUsernameAuthPending] = React.useState<boolean>(false);
  const [signInPending, setSignInPending] = React.useState<boolean>(false);
  const [processPending, setProcessPending] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<SetErrorsPayload>(defaultErrors);
  const [resetEmailOrPhone, setResetEmailOrPhone] = React.useState<string | undefined>(undefined);
  const [resetCountryAcronym, setResetCountryAcronym] = React.useState<string | undefined>(
    undefined,
  );
  const [pendingEmailOrPhone, setPendingEmailOrPhone] = React.useState<string | undefined>(
    undefined,
  );
  const [pendingCountryAcronym, setPendingCountryAcronym] = React.useState<string | undefined>(
    undefined,
  );
  const [pendingEmailOrPhoneSuccessful, _setPendingEmailOrPhoneSuccessful] =
    React.useState<boolean>(false);
  const [signInComponent, setSignInComponent] = React.useState<SIGN_IN_COMPONENT>(
    SIGN_IN_COMPONENT.SIGNIN,
  );
  const [successMessage, setSuccessMessage] = React.useState<string | undefined>(undefined);
  const [failureMessage, setFailureMessage] = React.useState<string | undefined>(undefined);
  const [oauthPending, setOauthPending] = React.useState<boolean>(readOauthPendingFlag);
  // True while an OAuth sign-in flow is initiating/in-flight (web full-page
  // redirect window, or native SafariViewController open). During this window
  // there is no OAuth signal on the page, so the stranded-pending cleanup below
  // must not treat `oauthPending` as abandoned and kill the spinner.
  const [oauthInitiating, setOauthInitiating] = React.useState<boolean>(false);

  const isOidcUser = Boolean(oidcAuth.user);

  const resetErrors = React.useCallback(() => {
    setErrors(defaultErrors);
    setSuccessMessage(undefined);
    setFailureMessage(undefined);
  }, []);

  const markOauthPending = React.useCallback((pending: boolean) => {
    writeOauthPendingFlag(pending);
    setOauthPending(pending);
  }, []);

  // Resolve the session from local token storage once at startup. The result
  // is definitive: authenticated (either stack has a session) or
  // unauthenticated. There is no optimistic fallback (MUS-264).
  useEffect(() => {
    void authService.bootstrapSession(AUTH_BOOTSTRAP_WATCHDOG_MS);
  }, []);

  // Keep persisted redux storage namespaced to the current user. index.tsx sets
  // this before the first rehydration; this effect covers everything after:
  // a fresh login (writes go to the new user's namespace before authenticated
  // startup fetches data) and logout (back to the unscoped namespace). Without
  // it, a second account signed in on the same session would persist into — and
  // later rehydrate from — the previous user's keys.
  const activeUserId = snapshot.profile?.sub;
  useEffect(() => {
    CapacitorStorage.setUserId(activeUserId);
  }, [activeUserId]);

  const purgeLocalAppState = React.useCallback(async () => {
    dispatch(clearAuthData());
    dispatch(clearLessonData());
    dispatch(clearAppData());
    dispatch(clearRepertoireData());
    dispatch({ type: PURGE, key: 'authPersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'mainAppPersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'repertoirePersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'lessonPersistance', result: () => {} });
    dispatch({ type: PURGE, key: 'tutorialPersistance', result: () => {} });
    // MUS-496: the remembered per-level tier lives in sessionStorage, which is
    // scoped to the tab and not to the user. Without this, signing out and
    // straight back in as someone else hands the new user the previous one's
    // position — and the createLevelSession saga clamps only to the level's max
    // tier, not to what this user has actually unlocked.
    clearTierMemory();
    await CapacitorStorage.clear();
  }, [dispatch]);

  const resetFormState = React.useCallback(() => {
    setConfirmationCode(undefined);
    setUsernameAuthPending(false);
    setSignInPending(false);
    setProcessPending(false);
    setErrors(defaultErrors);
    setSuccessMessage(undefined);
    setFailureMessage(undefined);
    setPendingEmailOrPhone(undefined);
  }, []);

  const logOut = React.useCallback(async () => {
    await authService.signOut();
    await purgeLocalAppState();
    resetFormState();
    markOauthPending(false);
    // No hard redirect: isAuthenticated flips false and ProtectedRoute
    // declaratively navigates protected routes to /signin.
  }, [purgeLocalAppState, resetFormState, markOauthPending]);

  // A live session died (refresh rejected). AuthService already tore down the
  // identity stacks; clean up app state so nothing stale survives.
  useEffect(() => {
    return authService.onSessionInvalid(() => {
      logger.warn('[Auth] Session invalidated — purging local app state');
      void purgeLocalAppState();
      resetFormState();
    });
  }, [purgeLocalAppState, resetFormState]);

  // Reconciliation (MUS-264): the session resolved to a definitive "none",
  // but signed-in app data survives in persisted storage — a zombie state the
  // old code could never escape. Purge it. Guards: an OAuth callback may be
  // mid-exchange (about to be authenticated), and a pending sign-up has no
  // session by design.
  useEffect(() => {
    const oauthFlowInFlight = hasAuthParams() || oidcAuth.isLoading || oauthPending;
    const signUpInFlight = Boolean(reduxAuth.tempSignUpPass || pendingEmailOrPhone);
    if (
      snapshot.status === 'unauthenticated' &&
      hasSignedInData &&
      !oauthFlowInFlight &&
      !signUpInFlight
    ) {
      logger.warn('[Auth] No session but persisted app data found — reconciling via purge');
      void purgeLocalAppState();
    }
  }, [
    snapshot.status,
    hasSignedInData,
    oidcAuth.isLoading,
    oauthPending,
    reduxAuth.tempSignUpPass,
    pendingEmailOrPhone,
    purgeLocalAppState,
  ]);

  const logInUsername = React.useCallback(
    async (emailOrPhone: string, password: string, countryAcronym: string) => {
      try {
        setSignInPending(true);
        // Clear any stale OIDC user so the session resolves from Amplify.
        await oidcManager.removeUser();

        try {
          await amplify.signIn({ emailOrPhone, password, countryAcronym });
        } catch (signInError) {
          // On app relaunch (esp. iPad), routing can bounce a still-valid
          // session to /signin. Cognito then rejects a fresh sign-in with
          // "There is already a signed in user". That's not a real failure —
          // recover the existing session instead of surfacing an error. MUS-248.
          if ((signInError as AuthError)?.name === 'UserAlreadyAuthenticatedException') {
            logger.warn('[Auth] logInUsername: already authenticated — recovering session');
          } else {
            throw signInError;
          }
        }
        // Confirmed success (signIn resolved, or an existing session was
        // recovered): remember email/password as the last-used method on this
        // device so the sign-in page can badge it next time.
        setLastLoginMethod('email');
        setPendingEmailOrPhone(undefined);
        dispatch(authActions.setTempSignUpPass(undefined));
        try {
          localStorage.removeItem('tempSignUpPass');
        } catch {
          // localStorage may be unavailable in some contexts
        }
        resetErrors();
        await authService.refreshSession();
        dispatch(authenticatedStartup());
      } catch (error) {
        const authError = error as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(error as AuthError);
        logger.error('Error logging in with username:', error);
        // as of amplify 6.0.0, the unverified error is now unauthenticated.
        // Keeping "user not confirmed" just in case.
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(emailOrPhone);
          if (isPhoneNumber(emailOrPhone)) {
            setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
          }
        } else {
          setErrors(handledAuthErrors);
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(error);
          }
        }
      } finally {
        setSignInPending(false);
      }
    },
    [dispatch, resetErrors],
  );

  const logInOidc = React.useCallback(
    async (identityProvider: 'Google' | 'SignInWithApple') => {
      const native = isCapacitorNative();
      markOauthPending(true);
      // Mark the flow in-flight synchronously, before any await, so the
      // stranded-pending cleanup doesn't clear the spinner the instant it is set.
      // Both paths start with no OAuth signal on the page: the web redirect hasn't
      // navigated yet, and the native SafariView has no main-webview signal.
      setOauthInitiating(true);
      // Stash the attempted provider (intent only). It is promoted to the
      // committed "last used" value on confirmed success (see the oidcAuth.user
      // effect); a failed/abandoned OAuth never records a last-used method.
      setPendingOauthMethod(identityProvider === 'Google' ? 'google' : 'apple');
      try {
        if (native) {
          // iOS/Android native: open Hosted UI in SafariView (Apple-approved).
          // The webview stays alive underneath, so in-memory state survives.
          const client = new OidcClient(oidcManager.settings);
          const req = await client.createSigninRequest({
            extraQueryParams: { identity_provider: identityProvider },
          });
          // The SafariView dismissing — success redirect or user backing out —
          // ends the in-flight window so the cleanup can clear a stranded spinner.
          await Browser.removeAllListeners();
          await Browser.addListener('browserFinished', () => {
            setOauthInitiating(false);
          });
          await Browser.open({ url: req.url });
        } else {
          // Web: full-page redirect navigates away (taking this flag with it). The
          // return URL carries ?code&state and sessionStorage carries pending.
          await oidcManager.signinRedirect({
            extraQueryParams: { identity_provider: identityProvider },
          });
        }
      } catch (e) {
        markOauthPending(false);
        setOauthInitiating(false);
        reportError(e, 'auth.oidcLogin');
        throw e;
      }
    },
    [markOauthPending],
  );

  const signUpUsername = React.useCallback(
    async (evt: SignUpPayload) => {
      try {
        setProcessPending(true);
        // Persist so we can log in after verification — including from the
        // verification tab (sessionStorage is per-tab) and after iOS Safari
        // page eviction.
        dispatch(authActions.setTempSignUpPass(evt.password));
        try {
          localStorage.setItem('tempSignUpPass', evt.password);
        } catch {
          // localStorage may be unavailable in some contexts
        }
        await amplify.signUpUser(evt);

        setPendingEmailOrPhone(evt.emailOrPhone);
        if (isPhoneNumber(evt.emailOrPhone)) {
          setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
        } else {
          setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
        }
      } catch (err) {
        const authError = err as AuthError;
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(evt.emailOrPhone);
        } else {
          const handledAuthErrors = handleAuthErrors(err as AuthError);
          setErrors(handledAuthErrors);
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      } finally {
        setProcessPending(false);
      }
    },
    [dispatch],
  );

  const confirmSignUpUsername = React.useCallback(
    async (evt: ConfirmSignUpPayload) => {
      const { phoneNumber, confirmationCode, countryAcronym } = evt;
      try {
        if (phoneNumber && confirmationCode && reduxAuth.tempSignUpPass) {
          setProcessPending(true);
          await amplify.confirmSignUp(phoneNumber, confirmationCode, countryAcronym);
          setPendingEmailOrPhone(undefined);
          setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
          await logInUsername(phoneNumber, reduxAuth.tempSignUpPass, countryAcronym);
        } else {
          const message = `No emailOrPhone or ConfirmationCode on confirm sign up: emailOrPhone: ${phoneNumber}, confirmatonCode: ${confirmationCode}`;
          logger.warn(message);
          awsRum?.recordError(new Error(message));
        }
      } catch (err) {
        const authError = err as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(phoneNumber);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      } finally {
        setProcessPending(false);
      }
    },
    [reduxAuth.tempSignUpPass, logInUsername],
  );

  const resendSignUpUsername = React.useCallback(
    async (emailOrPhone: string) => {
      try {
        await amplify.resendSignUp(emailOrPhone, pendingCountryAcronym);
      } catch (err) {
        const authError = err as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [pendingCountryAcronym],
  );

  const verifyPendingEmailOrPhone = React.useCallback(
    async (code: string) => {
      try {
        if (pendingEmailOrPhone) {
          await amplify.confirmSignUp(pendingEmailOrPhone, code);
        }
      } catch (err) {
        reportError(err, 'auth.verifyPending');
        throw err;
      }
      setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
    },
    [pendingEmailOrPhone],
  );

  const refreshUser = React.useCallback(async () => {
    try {
      await authService.refreshSession();
    } catch (err) {
      logger.error('failed to refresh user', err);
      reportError(err, 'auth.refreshUser');
    }
  }, []);

  const verifyAttributes = React.useCallback(async () => {
    try {
      if (isPhoneNumber(pendingEmailOrPhone)) {
        await amplify.verifyUserAttribute('phone_number');
      }
      if (isEmail(pendingEmailOrPhone)) {
        await amplify.verifyUserAttribute('email');
      }
    } catch (err) {
      reportError(err, 'auth.verifyAttributes');
      throw err;
    }
    setPendingEmailOrPhone(undefined);
    setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
  }, [pendingEmailOrPhone]);

  const verifyAttributesSubmit = React.useCallback(
    async (code: string) => {
      try {
        if (isPhoneNumber(pendingEmailOrPhone)) {
          await amplify.verifyCurrentUserAttributeSubmit('phone_number', code);
        }
        if (isEmail(pendingEmailOrPhone)) {
          await amplify.verifyCurrentUserAttributeSubmit('email', code);
        }
        await refreshUser();
        setPendingEmailOrPhone(undefined);
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          (authError.name === 'UserNotConfirmedException' ||
            authError.name === 'UserUnAuthenticatedException') &&
          !pendingEmailOrPhone
        ) {
          if (user?.attributes?.email) {
            setPendingEmailOrPhone(user?.attributes.email);
          } else if (user?.attributes?.phone_number) {
            setPendingEmailOrPhone(user?.attributes.phone_number);
          }
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [pendingEmailOrPhone, user, refreshUser],
  );

  const updateAuthDataCognito = React.useCallback(
    async (updateAuthData: { payload: UpdateAuthDataValues }) => {
      const values = updateAuthData.payload;
      const filteredValues = filterFalsy(pick(updateAuthData.payload, ['name', 'emailOrPhone']));
      const finalValues: { name?: string; email?: string; phone_number?: string } = {};
      setProcessPending(true);
      if (has(filteredValues, 'emailOrPhone') && isPhoneNumber(filteredValues['emailOrPhone'])) {
        const strippedNumber =
          '+' +
          codeFromCountryAcronym(updateAuthData.payload.countryAcronym) +
          String(filteredValues['emailOrPhone']).replace(/[^0-9.]/g, '');
        finalValues['phone_number'] = strippedNumber;
      }
      if (has(filteredValues, 'emailOrPhone') && isEmail(filteredValues['emailOrPhone'])) {
        finalValues['email'] = filteredValues['emailOrPhone'];
      }
      if (has(filteredValues, 'name')) {
        finalValues.name = filteredValues.name;
      }
      try {
        await amplify.updateUserAttributes(finalValues);
        await refreshUser();
        if (
          values?.emailOrPhone &&
          user?.attributes?.email !== values?.emailOrPhone &&
          user?.attributes?.phone_number !== values?.emailOrPhone
        ) {
          setPendingEmailOrPhone(values.emailOrPhone);
          setPendingCountryAcronym(values.countryAcronym);
        } else {
          setSuccessMessage('profile successfully updated');
        }
        setProcessPending(false);
      } catch (err) {
        setProcessPending(false);
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError?.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(values.emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [user, refreshUser],
  );

  const changePassword = React.useCallback(
    async (changePassword: { payload: ChangePasswordData }) => {
      const { currentPassword, newPassword } = changePassword.payload;
      try {
        await amplify.changePassword(currentPassword, newPassword);
        setSuccessMessage('Password successfully updated');
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          (authError.name === 'UserNotConfirmedException' ||
            authError.name === 'UserUnAuthenticatedException') &&
          !pendingEmailOrPhone
        ) {
          if (user?.attributes?.email) {
            setPendingEmailOrPhone(user?.attributes.email);
          } else if (user?.attributes?.phone_number) {
            setPendingEmailOrPhone(user?.attributes.phone_number);
          }
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [pendingEmailOrPhone, user],
  );

  const forgotPassword = React.useCallback(
    async (forgotPassword: { payload: ForgotPasswordData }) => {
      const { emailOrPhone, countryAcronym } = forgotPassword.payload;
      try {
        await amplify.forgotPassword(emailOrPhone, countryAcronym);
        setResetEmailOrPhone(emailOrPhone);
        setResetCountryAcronym(countryAcronym);
        setProcessPending(false);
        setSignInComponent(SIGN_IN_COMPONENT.VERIFICATION_CODE);
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError?.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [],
  );

  const submitNewPassword = React.useCallback(
    async (submitNewPassword: { payload: { password: string } }) => {
      const { password } = submitNewPassword.payload;
      const emailOrPhone =
        resetEmailOrPhone || user?.attributes?.email || user?.attributes?.phone_number;
      try {
        if (confirmationCode && emailOrPhone) {
          await amplify.submitForgotPassword(
            emailOrPhone,
            confirmationCode,
            password,
            resetCountryAcronym,
          );
          setSignInComponent(SIGN_IN_COMPONENT.SIGNIN);
        } else {
          const message = `No emailOrPhone or ConfirmationCode on submit new password: emailOrPhone: ${emailOrPhone}, confirmatonCode: ${confirmationCode}`;
          logger.warn(message);
          awsRum?.recordError(new Error(message));
        }
      } catch (err) {
        const authError = err as unknown as AuthError;
        const handledAuthErrors = handleAuthErrors(authError);
        setErrors(handledAuthErrors);
        if (
          authError?.name === 'UserNotConfirmedException' ||
          authError.name === 'UserUnAuthenticatedException'
        ) {
          setPendingEmailOrPhone(emailOrPhone);
        } else {
          if (Object.keys(handledAuthErrors).includes('unknown')) {
            awsRum?.recordError(err);
          }
        }
      }
    },
    [resetEmailOrPhone, user, confirmationCode, resetCountryAcronym],
  );

  // OAuth login completed (web callback processed, or native SafariView
  // roundtrip): fold the OIDC user into the session and start the app.
  useEffect(() => {
    if (oidcAuth.user) {
      markOauthPending(false);
      // Confirmed OAuth success: record the provider as the last-used method
      // (derived from the returned token claims, falling back to the stashed
      // redirect intent).
      commitOauthLoginMethod(oidcAuth.user.profile as Record<string, unknown>);
      void authService.refreshSession().then(() => {
        dispatch(authenticatedStartup());
      });
    }
    if (oidcAuth.error) {
      logger.error('[Auth] OIDC flow error', oidcAuth.error);
      reportError(oidcAuth.error, 'auth.oidcCompletion');
      markOauthPending(false);
    }
  }, [oidcAuth.user, oidcAuth.error, dispatch, markOauthPending]);

  // Clear OAuth params from the URL after the callback is processed.
  useEffect(() => {
    if (oidcAuth.isAuthenticated && window.location.search) {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.pathname);
    }
  }, [oidcAuth.isAuthenticated]);

  // A pending flag with no flow behind it (user hit back from the Hosted UI,
  // or the callback finished without a user) must not strand the spinner.
  // While a sign-in flow is initiating there is no OAuth signal on the page yet,
  // so wait until it ends (web navigates away; native flips this false on
  // browserFinished) before treating pending as stranded — otherwise this clears
  // the flag the instant logInOidc sets it and the spinner never shows.
  useEffect(() => {
    if (oauthInitiating) {
      return;
    }
    if (oauthPending && !oidcAuth.isLoading && !oidcAuth.user && !hasAuthParams()) {
      markOauthPending(false);
    }
  }, [oauthPending, oidcAuth.isLoading, oidcAuth.user, markOauthPending, oauthInitiating]);

  // Silent renew is handled by oidc-client-ts (automaticSilentRenew). We only
  // listen for its terminal failure, which means the refresh token is dead.
  useEffect(() => {
    const onSilentRenewError = (error: Error) => {
      logger.error('[Auth] Silent renew failed', error);
      if (isInvalidGrantOrUnauthorized(error)) {
        void authService.reportSessionInvalid();
      } else {
        reportError(error, 'auth.silentRenew');
      }
    };
    oidcManager.events.addSilentRenewError(onSilentRenewError);
    return () => {
      oidcManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, []);

  return (
    <AuthWrapperContext.Provider
      value={{
        isOidcUser,
        isAuthenticated,
        currentAuthToken: authService.getToken,
        user,
        logOut,
        signInRedirect: oidcAuth.signinRedirect,
        logInUsername,
        setConfirmationCode,
        confirmationCode,
        usernameAuthPending,
        processPending,
        errors,
        resetEmailOrPhone,
        resetCountryAcronym,
        pendingEmailOrPhone,
        pendingCountryAcronym,
        pendingEmailOrPhoneSuccessful,
        signInComponent,
        successMessage,
        setSignInComponent,
        failureMessage,
        refreshUser,
        verifyAttributes,
        verifyAttributesSubmit,
        updateAuthDataCognito,
        submitNewPassword,
        forgotPassword,
        changePassword,
        verifyPendingEmailOrPhone,
        resendSignUpUsername,
        confirmSignUpUsername,
        signUpUsername,
        logInOidc,
        resetErrors,
        setPendingEmailOrPhone,
        authInitialized,
        isLoading: Boolean(!authInitialized || oidcAuth.isLoading || signInPending),
        oauthPending,
        tempSignUpPass: reduxAuth.tempSignUpPass,
      }}
    >
      {children}
    </AuthWrapperContext.Provider>
  );
};

function AuthWrapperExport(props: { children: React.ReactNode }) {
  return (
    <AuthProvider userManager={oidcManager}>
      <AuthWrapperProvider>{props.children}</AuthWrapperProvider>
    </AuthProvider>
  );
}
export default AuthWrapperExport;
